import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Divider, 
  Button, 
  Card, 
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Chip,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import { 
  DarkMode as DarkModeIcon, 
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  MusicNote as MusicNoteIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Info as InfoIcon,
  SmartToy as SmartToyIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useAuth } from '../context';
import Layout from '../components/layout/Layout';
import { firebaseService } from '../services/firebase';

// Define available AI models
const AI_MODELS = [
  { id: 'o4-mini', name: 'o4-mini', provider: 'OpenAI', description: 'Mini reasoning model' },
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI', description: 'Latest multimodal model from OpenAI' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'OpenAI', description: 'Smaller, faster version of GPT-4o' },
  { id: 'gpt-4.1-nano', name: 'gpt-4.1 Nano', provider: 'OpenAI', description: 'Smallest, faster version of gpt-4.1' },
  { id: 'gpt-4.1-mini', name: 'gpt-4.1 Mini', provider: 'OpenAI', description: 'Smaller, faster version of gpt-4.1' },
  { id: 'gpt-4.1', name: 'gpt-4.1', provider: 'OpenAI', description: 'Full version of gpt-4.1' },
  { id: 'claude-3-5-sonnet-20240620', name: 'Claude 3.5 Sonnet', provider: 'Anthropic', description: 'High-quality model with strong reasoning' },
  { id: 'gemini-2.0-flash-001', name: 'Gemini 1.5 Flash', provider: 'Google', description: 'Fast, efficient model from Google' }
];

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for settings
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  
  // State for AI model selection
  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('preferred_ai_model') || 'gpt-4o';
  });
  
  // State for Green Tokens
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [isFirebaseLoggedIn, setIsFirebaseLoggedIn] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  
  // State for login dialog
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // State for logout confirmation dialog
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  // Load Green Tokens data on component mount
  useEffect(() => {
    const loadGreenTokensData = async () => {
      const isLoggedIn = firebaseService.isLoggedIn();
      setIsFirebaseLoggedIn(isLoggedIn);
      
      if (isLoggedIn) {
        const keys = firebaseService.getAllApiKeys();
        setApiKeys(keys);
      }
    };
    
    loadGreenTokensData();
  }, []);
  
  // Handle model change
  const handleModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newModel = event.target.value;
    setSelectedModel(newModel);
    
    // Save to localStorage for persistence
    localStorage.setItem('preferred_ai_model', newModel);
    
    // In a real implementation, we would update the OpenAI service here
    // For now, we'll just log the change
    console.log(`AI model changed to: ${newModel}`);
  };
  
  // Handle Firebase login
  const handleFirebaseLogin = async () => {
    setIsLoadingKeys(true);
    setLoginError('');
    
    try {
      const success = await firebaseService.login(email, password);
      
      if (success) {
        setLoginDialogOpen(false);
        setIsFirebaseLoggedIn(true);
        const keys = await firebaseService.fetchApiKeys();
        setApiKeys(keys);
      } else {
        setLoginError('Invalid email or password');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
      console.error('Firebase login error:', error);
    } finally {
      setIsLoadingKeys(false);
    }
  };
  
  // Handle Firebase logout
  const handleFirebaseLogout = async () => {
    await firebaseService.logout();
    setIsFirebaseLoggedIn(false);
    setApiKeys([]);
  };
  
  // Handle refresh API keys
  const handleRefreshApiKeys = async () => {
    setIsLoadingKeys(true);
    try {
      const keys = await firebaseService.fetchApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Error refreshing API keys:', error);
    } finally {
      setIsLoadingKeys(false);
    }
  };
  
  // Handle Spotify logout
  const handleLogout = () => {
    setLogoutDialogOpen(false);
    logout();
  };
  
  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Settings
        </Typography>
        
        {/* User Profile Section */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src={user?.images?.[0]?.url} 
              alt={user?.displayName || 'User'}
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Box>
              <Typography variant="h6">{user?.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
              {user?.product && (
                <Typography variant="caption" sx={{ 
                  display: 'inline-block',
                  bgcolor: user.product === 'premium' ? 'success.main' : 'info.main',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  mt: 1
                }}>
                  {user.product.charAt(0).toUpperCase() + user.product.slice(1)}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        
        {/* Green Tokens Section */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <KeyIcon sx={{ mr: 1 }} /> Green Tokens
        </Typography>
        
        <Card sx={{ mb: 4 }}>
          <CardContent>
            {isFirebaseLoggedIn ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">API Keys</Typography>
                  <Box>
                    <Tooltip title="Refresh API Keys">
                      <IconButton 
                        onClick={handleRefreshApiKeys} 
                        disabled={isLoadingKeys}
                        size="small"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      onClick={handleFirebaseLogout}
                      startIcon={<LogoutIcon />}
                      sx={{ ml: 1 }}
                    >
                      Disconnect
                    </Button>
                  </Box>
                </Box>
                
                {apiKeys.length > 0 ? (
                  <Box>
                    {apiKeys.map((key, index) => (
                      <Chip 
                        key={index}
                        label={`${key.substring(0, 5)}...${key.substring(key.length - 4)}`}
                        color="success"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {apiKeys.length} API {apiKeys.length === 1 ? 'key' : 'keys'} available for AI features
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    No API keys found. Please visit the Green Tokens dashboard to add keys.
                  </Alert>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Connect to Green Tokens to use your own API keys
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => setLoginDialogOpen(true)}
                  startIcon={<LoginIcon />}
                >
                  Connect Green Tokens
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Don't have an account? <Button 
                    size="small" 
                    onClick={() => firebaseService.redirectToSignup()}
                  >
                    Sign up
                  </Button>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        
        {/* AI Model Selection */}
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <SmartToyIcon sx={{ mr: 1 }} /> AI Model Settings
        </Typography>
        
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <FormControl component="fieldset">
              <FormLabel component="legend">Select AI Model</FormLabel>
              <RadioGroup
                value={selectedModel}
                onChange={handleModelChange}
              >
                {AI_MODELS.map((model) => (
                  <FormControlLabel
                    key={model.id}
                    value={model.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{model.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {model.provider} • {model.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>
        
        {/* App Settings */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          App Settings
        </Typography>
        
        <List>
          <ListItem>
            <ListItemIcon>
              {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
            </ListItemIcon>
            <ListItemText 
              primary="Dark Mode" 
              secondary="Toggle between light and dark theme"
            />
            <Switch 
              checked={darkMode} 
              onChange={(e) => setDarkMode(e.target.checked)} 
            />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemIcon>
              <MusicNoteIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Music Provider" 
              secondary="Currently using Spotify"
            />
          </ListItem>
        </List>
        
        {/* About Section */}
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          About
        </Typography>
        
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="body2" paragraph>
              <strong>Tunes with Otto</strong> - Version 0.1.0
            </Typography>
            <Typography variant="body2" color="text.secondary">
              An AI-powered music companion that helps you discover new music, create playlists, and learn about your favorite artists.
            </Typography>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<InfoIcon />}>
                Privacy Policy
              </Button>
              <Button size="small" startIcon={<InfoIcon />}>
                Terms of Service
              </Button>
            </Box>
          </CardContent>
        </Card>
        
        {/* Logout Button */}
        <Button 
          variant="outlined" 
          color="error" 
          startIcon={<LogoutIcon />}
          onClick={() => setLogoutDialogOpen(true)}
          fullWidth={isMobile}
          sx={{ mt: 2 }}
        >
          Logout from Spotify
        </Button>
        
        {/* Logout Confirmation Dialog */}
        <Dialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
        >
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to log out? You'll need to reconnect with Spotify to use the app again.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLogout} color="error">
              Logout
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Green Tokens Login Dialog */}
        <Dialog
          open={loginDialogOpen}
          onClose={() => setLoginDialogOpen(false)}
        >
          <DialogTitle>Connect to Green Tokens</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter your Green Tokens credentials to use your own API keys.
            </DialogContentText>
            
            {loginError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginError}
              </Alert>
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ 
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  width: '100%'
                }}
              />
            </FormControl>
            
            <FormControl fullWidth>
              <FormLabel>Password</FormLabel>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ 
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  width: '100%'
                }}
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoginDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleFirebaseLogin} 
              variant="contained" 
              color="primary"
              disabled={isLoadingKeys || !email || !password}
            >
              {isLoadingKeys ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Settings;
